/**
 * GuestBanner — subtle banner rendered above guest-mode pages reminding the
 * anonymous visitor that interactions require sign-in.
 *
 * The banner's login CTA preserves the current URL via the `?next=` query
 * parameter so that file `04`'s interaction-auth gating can resume the user
 * exactly where they left off after a successful login.
 *
 * RTL-correct: uses logical inline-margin (`ms-*`/`me-*`) and avoids physical
 * directional classes per `.claude/rules/rtl-support-arabic.md`.
 */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { buildLoginUrlPreservingReturn } from "../../lib/guestMode/returnUrl";

export interface GuestBannerProps {
  /** Override the URL to return to after login. Defaults to current location. */
  returnTo?: string;
  /** Optional slot for additional CTAs (e.g. a register button). */
  extraAction?: ReactNode;
}

export function GuestBanner({ returnTo, extraAction }: GuestBannerProps) {
  const { t } = useTranslation("common");
  const loginHref = buildLoginUrlPreservingReturn(returnTo);
  const registerHref = buildLoginUrlPreservingReturn(returnTo, "register");

  return (
    <aside
      role="status"
      aria-live="polite"
      data-testid="guest-banner"
      className="flex flex-wrap items-center gap-3 border-b bg-muted/60 px-4 py-2 text-sm text-muted-foreground"
    >
      <p className="me-auto">
        {t(
          "guestMode.bannerMessage",
          "You're browsing as a guest — sign in to interact.",
        )}
      </p>
      <a
        href={loginHref}
        className="inline-flex min-h-9 items-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        {t("guestMode.signIn", "Sign in")}
      </a>
      <a
        href={registerHref}
        className="inline-flex min-h-9 items-center rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
      >
        {t("guestMode.createAccount", "Create account")}
      </a>
      {extraAction}
    </aside>
  );
}
